import type {
  FieldErrors,
  UseFormRegister,
  RegisterOptions,
} from "react-hook-form"
import { useEffect, useRef, useId } from "react"
import classNames from "classnames"
import { RiErrorWarningFill, RiQuestionFill } from "react-icons/ri"
import { Tooltip } from "react-tooltip"
import * as styles from "@/styles/modules/Input.module.scss"

const Input = ({
  autoFocus,
  caption,
  className,
  defaultValue,
  disabled,
  errors,
  id,
  inputWrapperClass,
  label,
  name,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  raised,
  readOnly,
  register,
  stacked,
  tooltip,
  type = "text",
  validation,
  wrapperClass,
}: {
  autoFocus?: boolean
  caption?: string
  className?: string
  defaultValue?: string
  disabled?: boolean
  errors: FieldErrors
  id?: string
  inputWrapperClass?: string
  label?: string
  name: string
  onChange?: React.ChangeEventHandler
  onFocus?: React.FocusEventHandler
  onBlur?: React.FocusEventHandler
  placeholder?: string
  raised?: boolean
  readOnly?: boolean
  register: UseFormRegister<Record<string, any>>
  stacked?: boolean
  tooltip?: string
  type?: string
  validation: RegisterOptions
  wrapperClass?: string
}) => {
  const error = errors[name]
  const { ref, ...rest } = register(name, {
    ...validation,
    ...(onChange ? { onChange: e => onChange(e) } : {}),
  })

  const tooltipId = useId()

  const wrapperClasses = `${styles.wrapper} 
     ${stacked ? styles.stacked : ""} 
     ${wrapperClass ? wrapperClass : ""}`

  const inputClasses = `${styles.input} 
    ${raised ? styles.raised : ""} 
    ${className ? className : ""}`

  const inputRef = useRef(null)

  useEffect(() => {
    if (autoFocus) {
      inputRef.current.focus()
    }
  }, [autoFocus])

  return (
    <>
      <div className={wrapperClasses}>
        {label && (
          <label htmlFor={name} className={styles.label}>
            {label}{" "}
            {!!validation?.required && (
              <span style={{ color: "#ca1b3b" }}>*</span>
            )}
          </label>
        )}
        <div className={classNames(styles.inputWrapper, inputWrapperClass)}>
          <input
            className={inputClasses}
            defaultValue={defaultValue}
            disabled={disabled}
            id={id || name}
            placeholder={placeholder}
            onBlur={onBlur}
            onFocus={onFocus}
            readOnly={readOnly}
            ref={e => {
              ref(e)
              inputRef.current = e
            }}
            type={type}
            {...rest}
          />
          <Tooltip className="donut-tooltip" id={tooltipId} place="left" />
          {error ? (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <a
              className={styles.tooltipAnchor}
              data-tooltip-id={tooltipId}
              data-tooltip-content={error.message.toString()}
            >
              <RiErrorWarningFill
                className={classNames(styles.tooltip, styles.error)}
              />
            </a>
          ) : tooltip ? (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <a
              className={styles.tooltipAnchor}
              data-tooltip-id={tooltipId}
              data-tooltip-content={tooltip}
            >
              <RiQuestionFill className={classNames(styles.tooltip)} />
            </a>
          ) : null}
        </div>
        {caption && <p className={`caption ${styles.caption}`}>{caption}</p>}
      </div>
    </>
  )
}

export default Input
